import React from 'react'
import styled from 'styled-components'

import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import PrismicLink from '@/components/prismic-link/'
import {Text} from '@/components/text'
import {focusStyles} from '@/styles/utils'
import {radii, colors, tokens} from '@/theme'
import mq from '@/utils/media-query'

import {Stars} from '../stars'

const bgColors = {
    white: colors.background.default,
    grey: colors.background.muted,
}

type BgColor = keyof typeof bgColors

export interface QuoteCardProps {
    headline: string
    numberOfStars: number
    description: string
    ctaLink?: PrismicLinkProps
    author: string
    subtitle: string
    Image?: React.ComponentType<any>
    bgColor?: BgColor
}

export const QuoteCard = ({
    headline,
    numberOfStars,
    description,
    ctaLink,
    author,
    subtitle,
    Image,
    bgColor = 'grey',
}: QuoteCardProps) => (
    <Wrapper $bgColor={bgColor}>
        <Headline $variant="paragraph-large-medium">{headline}</Headline>
        <Stars numberOfStars={numberOfStars} />
        <Description $variant="paragraph-base">{description}</Description>
        {ctaLink && (
            <AuthorButton {...ctaLink}>
                <div>
                    <Text $variant="paragraph-base">{author}</Text>
                    <Text $variant="label-base" $color="muted">
                        {subtitle}
                    </Text>
                </div>

                {Image && <StyledImage as={Image} />}
            </AuthorButton>
        )}
    </Wrapper>
)

const MOBILE_BREAKPOINT = 'md'

type WrapperProps = {
    $bgColor: BgColor
}

const Wrapper = styled.div<WrapperProps>`
    background-color: ${({$bgColor}) => bgColors[$bgColor]};
    padding: ${tokens.spacing20};
    border-radius: ${radii.arc20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        padding: ${tokens.spacing40};
    }
`

const AuthorButton = styled(PrismicLink)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: ${tokens.spacing20};

    &:focus {
        ${focusStyles()}
        border-radius: ${tokens.spacing4};
    }

    ${mq.min(MOBILE_BREAKPOINT)} {
        margin-top: ${tokens.spacing32};
    }
`

const StyledImage = styled.img`
    aspect-ratio: 1/1;
    object-fit: cover;
    object-position: center;
    width: 40px;
`

const Description = styled(Text)`
    margin-top: ${tokens.spacing12};

    ${mq.min(MOBILE_BREAKPOINT)} {
        margin-top: ${tokens.spacing20};
    }
`

const Headline = styled(Text)`
    margin-bottom: ${tokens.spacing4};

    ${mq.min(MOBILE_BREAKPOINT)} {
        margin-bottom: ${tokens.spacing10};
    }
`
